import {
	useApiGetBiDashboard,
	useApiGetBreadcrumbTeam,
} from '@repo/api-codegen';
import {
	isEmoji,
	stringToIconEmojiSelectorValue,
} from '@repo/common/components/IconEmojiSelector/utils';
import type { BreadcrumbCrumb } from '@repo/foundations';
import { useMemo } from 'react';
import { useEntityBreadcrumbTeam } from '../../../hooks/useEntityBreadcrumbs';

interface UseBiDashboardBreadcrumbsProps {
	dashboardId?: string;
}

export function useBiDashboardBreadcrumbs({
	dashboardId,
}: UseBiDashboardBreadcrumbsProps) {
	const { teamBreadcrumb: preferredTeamId } = useEntityBreadcrumbTeam();

	// Query dashboard if dashboardId is provided
	const { data: dashboard } = useApiGetBiDashboard(
		{
			pathParams: {
				biDashboardId: dashboardId ?? '',
			},
		},
		{
			enabled: !!dashboardId,
		}
	);

	const entity = dashboard?.secoda_entity;

	const { data: team, isLoading: isTeamLoading } = useApiGetBreadcrumbTeam(
		{
			pathParams: {
				entityId: entity?.id ?? '',
			},
			queryParams: {
				preferred_team_id: preferredTeamId,
			},
		},
		{
			enabled: !!entity?.id,
		}
	);

	const icon = useMemo(() => {
		if (!entity?.icon) return undefined;
		const iconValue = stringToIconEmojiSelectorValue(entity.icon);
		return isEmoji(iconValue) ? iconValue.emoji : undefined;
	}, [entity?.icon]);

	const breadcrumbs: BreadcrumbCrumb[] | undefined = useMemo(() => {
		if (!entity || !dashboardId) {
			return undefined;
		}

		return [
			{
				icon: team?.icon || undefined,
				title: team?.name ?? '',
				href: `/teams/${team?.id}/`,
			},
			{
				title: 'Dashboards',
				href: `/teams/${team?.id}/bi/dashboards`,
			},
			{
				title: entity.title ?? 'Untitled',
				icon: entity.icon ? icon : undefined,
				href: `/bi/dashboards/${dashboardId}`,
			},
		];
	}, [dashboardId, entity, icon, team?.icon, team?.id, team?.name]);

	return {
		breadcrumbs,
		dashboard,
		team,
		isLoading: isTeamLoading,
	};
}
